// frontend-react/src/pages/Settings.js (ΠΛΗΡΕΣ ΚΑΙ ΕΝΗΜΕΡΩΜΕΝΟ)

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../api/api';
import { AuthContext } from '../context/AuthContext';
import './Settings.css';
import { FiLink, FiShield, FiTarget, FiTrash2, FiPlus, FiSave, FiLock, FiMail, FiRepeat, FiCheckCircle, FiCpu, FiDatabase } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import TwoFactorAuthModal from '../components/TwoFactorAuthModal';
import toast from 'react-hot-toast';

const Settings = () => {
    const { user, fetchUser, isLocalExecution } = useContext(AuthContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [connections, setConnections] = useState([]);
    const [globalSettings, setGlobalSettings] = useState(null);
    const [riskProfiles, setRiskProfiles] = useState([]);
    const [marketDataStatus, setMarketDataStatus] = useState(null);
    const [productStatus, setProductStatus] = useState(null);
    const [systemStatus, setSystemStatus] = useState(null);
    const [researchStatus, setResearchStatus] = useState(null);
    const [fmpKey, setFmpKey] = useState('');
    
    const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
    
    const [showAlpacaForm, setShowAlpacaForm] = useState(false);
    const [alpacaForm, setAlpacaForm] = useState({ account_name: '', api_key: '', secret_key: '', is_paper: true });
    const [showIbkrForm, setShowIbkrForm] = useState(false);
    const [ibkrForm, setIbkrForm] = useState({ account_name: '', host: '127.0.0.1', port: 7497, client_id: 1 });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [connRes, settingsRes, profilesRes, marketDataRes, productRes, systemRes, researchRes] = await Promise.all([
                isLocalExecution ? api.getBrokerConnections() : Promise.resolve({ data: [] }),
                api.getGlobalSettings(),
                api.getRiskProfiles(),
                isLocalExecution ? api.getMarketDataProviderStatus() : Promise.resolve({ data: null }),
                isLocalExecution ? api.getLocalProductStatus() : Promise.resolve({ data: null }),
                isLocalExecution ? api.getLocalSystemStatus() : Promise.resolve({ data: null }),
                isLocalExecution ? api.getResearchValidationStatus() : Promise.resolve({ data: null })
            ]);
            setConnections(connRes.data);
            setGlobalSettings(settingsRes.data);
            setRiskProfiles(profilesRes.data);
            setMarketDataStatus(marketDataRes.data);
            setProductStatus(productRes.data);
            setSystemStatus(systemRes.data);
            setResearchStatus(researchRes.data);
        } catch (error) { 
            console.error("Failed to fetch settings", error); 
            toast.error("Failed to load settings.");
        }
        finally { setLoading(false); }
    }, [isLocalExecution]);

    useEffect(() => { fetchData(); }, [fetchData]);
    
    const handleRefreshUserData = () => {
        if(fetchUser) fetchUser(); 
        setIs2FAModalOpen(false); 
    };

    const handleGlobalSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setGlobalSettings(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };
    
    const handleSaveGlobalSettings = async () => {
        const settingsToSave = { ...globalSettings, 
            portfolio_value: parseFloat(globalSettings.portfolio_value),
            max_pos_percent: parseFloat(globalSettings.max_pos_percent),
            max_daily_loss_percent: parseFloat(globalSettings.max_daily_loss_percent),
            risk_value: parseFloat(globalSettings.risk_value),
            portfolio_risk_limit: globalSettings.portfolio_risk_limit === '' || globalSettings.portfolio_risk_limit == null ? null : Number(globalSettings.portfolio_risk_limit),
            cost_reserve: Number(globalSettings.cost_reserve ?? 0),
            account_currency: 'USD'
         };
        const promise = api.updateGlobalSettings(settingsToSave);
        toast.promise(promise, {
            loading: 'Saving settings...',
            success: 'Settings saved!',
            error: 'Error saving settings.'
        });
    };
    
    const handleProfileNameChange = (profileIndex, newName) => {
        const updatedProfiles = [...riskProfiles];
        updatedProfiles[profileIndex].name = newName;
        setRiskProfiles(updatedProfiles);
    };
    
    const handleTargetValueChange = (profileIndex, currentKey, field, value) => {
        const updatedProfiles = [...riskProfiles];
        const profile = updatedProfiles[profileIndex];
        const newTargets = {};

        Object.entries(profile.targets).forEach(([key, pct]) => {
            if (key === currentKey) {
                if (field === 'r') {
                    newTargets[value] = pct;
                } else { // field === 'pct'
                    newTargets[key] = parseFloat(value) / 100 || 0;
                }
            } else {
                newTargets[key] = pct;
            }
        });

        profile.targets = newTargets;
        setRiskProfiles(updatedProfiles);
    };

    const handleAddTarget = (profileIndex) => {
        const updatedProfiles = [...riskProfiles];
        const newTargetKey = `new_${Object.keys(updatedProfiles[profileIndex].targets).length}`;
        updatedProfiles[profileIndex].targets[newTargetKey] = 0;
        setRiskProfiles(updatedProfiles);
    };

    const handleRemoveTarget = (profileIndex, rTarget) => {
        const updatedProfiles = [...riskProfiles];
        delete updatedProfiles[profileIndex].targets[rTarget];
        setRiskProfiles(updatedProfiles);
    };

    const handleSaveRiskProfiles = async () => {
        const cleanedProfiles = riskProfiles.map(profile => {
            const cleanedTargets = {};
            Object.entries(profile.targets).forEach(([key, value]) => {
                const cleanKey = parseFloat(key.toString().replace(/[^0-9.]/g, ''));
                if (!isNaN(cleanKey) && cleanKey > 0) {
                    cleanedTargets[cleanKey] = value;
                }
            });
            return { ...profile, targets: cleanedTargets };
        });

        const promise = api.updateRiskProfiles(cleanedProfiles);
        toast.promise(promise, {
            loading: 'Saving risk profiles...',
            success: (res) => {
                fetchData();
                return 'Risk profiles saved!';
            },
            error: 'Error saving risk profiles.'
        });
    };

    const handleAddConnection = async (e, broker) => {
        e.preventDefault();
        let payload;
        if (broker === 'Alpaca') {
            payload = { broker_name: 'Alpaca', account_name: alpacaForm.account_name, is_paper: alpacaForm.is_paper, credentials: { api_key: alpacaForm.api_key, secret_key: alpacaForm.secret_key } };
        } else {
            payload = { broker_name: 'Interactive Brokers', account_name: ibkrForm.account_name, is_paper: true, credentials: { host: ibkrForm.host, port: parseInt(ibkrForm.port), client_id: parseInt(ibkrForm.client_id) } };
        }
        const promise = api.addBrokerConnection(payload);
        toast.promise(promise, {
            loading: `Adding ${broker} connection...`,
            success: () => { fetchData(); setShowAlpacaForm(false); setShowIbkrForm(false); return `${broker} connection added.`; },
            error: (err) => err.response?.data?.detail || `Failed to add ${broker} connection.`
        });
    };

    const handleSaveFmp = async (e) => {
        e.preventDefault();
        const key = fmpKey.trim();
        if (!key) {
            toast.error('Enter your FMP API key.');
            return;
        }
        const promise = api.configureFmpProvider(key);
        toast.promise(promise, {
            loading: 'Validating FMP connection...',
            success: (res) => {
                setFmpKey('');
                fetchData();
                const d = res.data;
                return `FMP connected · AAPL ${d.sample_price ?? 'quote OK'} · ${d.latency_ms} ms`;
            },
            error: (err) => err.response?.data?.detail || 'FMP validation failed.'
        });
    };

    const handleTestFmp = async () => {
        const promise = api.testFmpProvider();
        toast.promise(promise, {
            loading: 'Testing FMP...',
            success: (res) => {
                const d = res.data;
                return `FMP OK · AAPL ${d.sample_price ?? 'quote OK'} · ${d.latency_ms} ms`;
            },
            error: (err) => err.response?.data?.detail || 'FMP test failed.'
        });
    };

    const handleRemoveFmp = async () => {
        if (!window.confirm('Remove the locally stored FMP API key?')) return;
        const promise = api.removeFmpProvider();
        toast.promise(promise, {
            loading: 'Removing FMP key...',
            success: () => { fetchData(); return 'FMP key removed.'; },
            error: (err) => err.response?.data?.detail || 'Could not remove FMP key.'
        });
    };

    const handleTestConnection = async (id) => {
        const promise = api.testBrokerConnection(id);
        toast.promise(promise, {
            loading: 'Testing IBKR connection...',
            success: (res) => {
                const d = res.data;
                return `IBKR connected · ${d.accounts?.join(', ') || 'account detected'} · Equity ${Number(d.net_liquidation || 0).toLocaleString()}`;
            },
            error: (err) => err.response?.data?.detail || 'IBKR connection test failed.'
        });
    };

    const handleDeleteConnection = async (id) => {
        if (window.confirm('Delete this connection?')) {
            const promise = api.deleteBrokerConnection(id);
            toast.promise(promise, {
                loading: 'Deleting connection...',
                success: () => { fetchData(); return 'Connection deleted.'; },
                error: "Failed to delete connection."
            });
        }
    };

    const handleSetActive = async (id) => {
        const promise = api.setActiveBroker(id);
        toast.promise(promise, {
            loading: 'Setting active broker...',
            success: () => { fetchData(); return 'Active broker set.'; },
            error: "Failed to set active broker."
        });
    };

    const handleAutoConfigureIbkr = async () => {
        const promise = api.autoConfigureIbkrPaper();
        toast.promise(promise, {
            loading: 'Looking for IBKR Paper on this computer...',
            success: (res) => {
                fetchData();
                return res.data?.configured
                    ? `IBKR Paper detected · ${res.data.label} · port ${res.data.port}`
                    : 'No IBKR Paper listener found. Open TWS Paper or IB Gateway Paper first.';
            },
            error: (err) => err.response?.data?.detail || 'IBKR auto-detection failed.'
        });
    };

    const handleBackup = async () => {
        const promise = api.createLocalBackup();
        toast.promise(promise, {
            loading: 'Creating local backup...',
            success: (res) => `Backup created · ${res.data.path}`,
            error: (err) => err.response?.data?.detail || 'Backup failed.'
        });
    };

    if (loading || !globalSettings || !user) return <div style={{ display: 'flex', justifyContent: 'center', marginTop: '3rem' }}><LoadingSpinner /></div>;

    return (
        <div className={`workspace-page settings-stack ${isLocalExecution ? 'local-mode' : ''}`}>
            <div className="page-header">
                <div className="page-header-copy">
                    <div className="eyebrow">System configuration</div>
                    <h1 className="page-title">Settings</h1>
                    <p className="page-description">Configure data, broker execution and risk. Local mode keeps account/security noise out of the workstation.</p>
                </div>
            </div>

            {isLocalExecution && (
                <div className="card quick-setup-card">
                    <div className="quick-setup-head">
                        <div>
                            <div className="eyebrow">Owner local setup</div>
                            <h2>One-key workstation setup</h2>
                            <p className="page-subtitle">FMP is the only key needed for the current-market scanner. Historical filter validation is a separate point-in-time data workflow and remains gated until a research provider passes acceptance.</p>
                        </div>
                        <div className="version-chip">{systemStatus?.version || 'local'}</div>
                    </div>

                    <div className="setup-checks">
                        <div className={`setup-check ${productStatus?.checks?.market_data ? 'done' : ''}`}>
                            <FiDatabase/>
                            <div><strong>Market Data</strong><span>{productStatus?.checks?.market_data ? 'FMP connected' : 'Add FMP key below'}</span></div>
                            {productStatus?.checks?.market_data && <FiCheckCircle/>}
                        </div>
                        <div className={`setup-check ${productStatus?.checks?.ibkr_paper_configured ? 'done' : ''}`}>
                            <FiCpu/>
                            <div><strong>IBKR Paper</strong><span>{productStatus?.checks?.ibkr_paper_configured ? 'Configured automatically' : 'Open TWS/Gateway Paper, then detect'}</span></div>
                            {productStatus?.checks?.ibkr_paper_configured && <FiCheckCircle/>}
                        </div>
                        <div className={`setup-check ${productStatus?.checks?.paper_lock ? 'done' : ''}`}>
                            <FiShield/>
                            <div><strong>Safety Lock</strong><span>{productStatus?.checks?.paper_lock ? 'Paper-only lock active' : 'Review LIVE safety'}</span></div>
                            {productStatus?.checks?.paper_lock && <FiCheckCircle/>}
                        </div>
                    </div>

                    <div className="quick-setup-actions">
                        <button className="ws-button" onClick={handleAutoConfigureIbkr}><FiCpu/> Auto-detect IBKR Paper</button>
                        <button className="ws-button" onClick={handleBackup}><FiSave/> Backup local state</button>
                        <span className={`ready-pill ${productStatus?.ready_for_intelligence ? 'ready' : ''}`}>
                            Intelligence {productStatus?.ready_for_intelligence ? 'READY' : 'WAITING FOR FMP'}
                        </span>
                    </div>
                </div>
            )}
            
            <div className="card local-hide-saas">
                <h2><FiLock className="icon"/> Security</h2>
                <div className="connection-item">
                    <div>
                        <strong>Two-Factor Authentication (2FA)</strong>
                        <p style={{fontSize: '0.9rem', color: 'var(--text-secondary)', margin: '0.3rem 0 0 0'}}>
                            Add an extra layer of security to your account.
                        </p>
                    </div>
                    <div className="connection-actions">
                        {user.is_two_factor_enabled ? 
                            <span className="tag active">✓ Enabled</span> : 
                            <span className="tag paper">Disabled</span>
                        }
                        <button onClick={() => setIs2FAModalOpen(true)} className="action-button">Manage</button>
                    </div>
                </div>
            </div>

            <div className="card local-hide-saas" style={{marginTop: '2rem'}}>
                <h2><FiMail className="icon"/> Notification Preferences</h2>
                <div className="form-grid two-cols">
                    <div className="form-group checkbox-group">
                        <input 
                            type="checkbox" 
                            name="notifications_email_trades" 
                            id="email-trades-check"
                            checked={globalSettings.notifications_email_trades || false} 
                            onChange={handleGlobalSettingsChange} 
                        />
                        <label htmlFor="email-trades-check">Receive Email for Trade Actions (Open/Close/Partials)</label>
                    </div>
                    <div className="form-group checkbox-group">
                         <input 
                            type="checkbox" 
                            name="notifications_email_summaries" 
                            id="email-summaries-check"
                            checked={globalSettings.notifications_email_summaries || false} 
                            onChange={handleGlobalSettingsChange} 
                        />
                        <label htmlFor="email-summaries-check">Receive Daily/Hourly Summary Emails</label>
                    </div>
                </div>
                 <div className="settings-actions">
                    <button className="control-button" onClick={handleSaveGlobalSettings}><FiSave/> Save Preferences</button>
                 </div>
            </div>
            
            {isLocalExecution && (
                <div className="card" style={{marginTop: '2rem'}}>
                    <h2><FiLink className="icon"/> Market Data</h2>
                    <p className="page-subtitle" style={{margin: '0 0 1rem 0'}}>
                        FMP powers the current whole-market scanner, regime and opening RVOL. It is not treated as the historical point-in-time research dataset. The key is encrypted on this computer.
                    </p>

                    <div className="connection-item">
                        <div>
                            <strong>Financial Modeling Prep</strong>
                            <div style={{fontSize:'0.9rem', color:'var(--text-secondary)', marginTop:'.25rem'}}>
                                {marketDataStatus?.fmp_configured
                                    ? `FMP connected · ${marketDataStatus.credential_source || 'local'} · ${marketDataStatus.entitlement_mode || 'personal_byok'}`
                                    : marketDataStatus?.synthetic_data
                                        ? 'Synthetic Demo active · no FMP key configured'
                                        : 'FMP not configured'}
                            </div>
                        </div>
                        <div className="connection-actions">
                            {marketDataStatus?.fmp_configured ? <span className="tag active">✓ FMP Active</span> : marketDataStatus?.synthetic_data ? <span className="tag paper">Synthetic</span> : null}
                            {marketDataStatus?.fmp_configured && <button onClick={handleTestFmp} className="action-button">Test</button>}
                            {marketDataStatus?.fmp_configured && marketDataStatus?.credential_source !== 'environment' &&
                                <button onClick={handleRemoveFmp} className="icon-button danger"><FiTrash2/></button>}
                        </div>
                    </div>

                    {!marketDataStatus?.fmp_configured && (
                        <form onSubmit={handleSaveFmp} className="add-connection-form">
                            <input
                                type="password"
                                placeholder="Paste FMP API Key"
                                value={fmpKey}
                                onChange={e => setFmpKey(e.target.value)}
                                autoComplete="off"
                                required
                            />
                            <button type="submit" className="control-button">Connect & Validate FMP</button>
                        </form>
                    )}
                </div>
            )}

            {isLocalExecution && (
                <div className="card research-data-card">
                    <div className="research-data-head">
                        <div>
                            <h2><FiDatabase className="icon"/> Historical Research Data</h2>
                            <p className="page-subtitle" style={{margin: '0'}}>
                                Separate from the current-market feed. Only accepted point-in-time historical releases may be used to validate or change AlgoStock filters.
                            </p>
                        </div>
                        <span className={`research-data-state ${researchStatus?.provider_accepted ? 'ready' : 'blocked'}`}>
                            {researchStatus?.provider_accepted ? 'READY FOR EXPERIMENTS' : 'DATA BLOCKED'}
                        </span>
                    </div>
                    <div className="research-data-summary">
                        <div><span>Provider gates</span><strong>{researchStatus?.provider_gates?.accepted || 0}/{researchStatus?.provider_gates?.total || 22}</strong></div>
                        <div><span>Candidate signals</span><strong>{researchStatus?.signals?.total || 78}</strong></div>
                        <div><span>Phase-1 testable</span><strong>{researchStatus?.signals?.phase_counts?.PHASE1 || 51}</strong></div>
                        <div><span>Accepted releases</span><strong>{researchStatus?.store?.accepted_release_count || 0}</strong></div>
                    </div>
                    <div className="research-data-actions">
                        <span>Production filter remains unfrozen until empirical validation survives the declared protocol.</span>
                        <button className="ws-button" onClick={()=>navigate('/research-validation')}>Open Validation</button>
                    </div>
                </div>
            )}

            {isLocalExecution && <div id="broker-connections" className="card" style={{marginTop: '2rem'}}>
                <h2><FiLink className="icon"/> IBKR Execution</h2>
                {isLocalExecution && (
                    <p className="page-subtitle" style={{marginTop:0}}>
                        Local V1 execution supports IBKR only. Broker execution is optional; Market Intelligence works without any broker connection.
                    </p>
                )}
                {connections.map(conn => (
                    <div key={conn.id} className="connection-item">
                        <div><strong>{conn.account_name}</strong> ({conn.broker_name})<span className={`tag ${conn.is_paper ? 'paper' : 'live'}`}>{conn.is_paper ? 'Paper' : 'Live'}</span></div>
                        <div className="connection-actions">
                            {conn.broker_name === 'Interactive Brokers' && <button onClick={() => handleTestConnection(conn.id)} className="action-button">Test</button>}
                            {isLocalExecution && conn.broker_name !== 'Interactive Brokers'
                                ? <span className="tag paper">Legacy / unsupported in Local V1</span>
                                : conn.is_active
                                    ? <span className="tag active">✓ Active</span>
                                    : <button onClick={() => handleSetActive(conn.id)} className="action-button">Set Active</button>}
                            <button onClick={() => handleDeleteConnection(conn.id)} className="icon-button danger"><FiTrash2/></button>
                        </div>
                    </div>
                ))}
                {connections.length === 0 && <p className="empty-state-text">No broker connections found.</p>}
                
                <div className="add-buttons-container">
                    {!isLocalExecution && <button className="control-button small" onClick={() => {setShowAlpacaForm(!showAlpacaForm); setShowIbkrForm(false)}}><FiPlus/> Add Alpaca</button>}
                    <button className="control-button small" onClick={() => {setShowIbkrForm(!showIbkrForm); setShowAlpacaForm(false)}}><FiPlus/> Add IBKR</button>
                </div>

                {!isLocalExecution && showAlpacaForm && (
                    <form onSubmit={(e) => handleAddConnection(e, 'Alpaca')} className="add-connection-form">
                        <input type="text" placeholder="Account Name (e.g., My Alpaca Paper)" value={alpacaForm.account_name} onChange={e => setAlpacaForm({...alpacaForm, account_name: e.target.value})} required />
                        <input type="text" placeholder="API Key" value={alpacaForm.api_key} onChange={e => setAlpacaForm({...alpacaForm, api_key: e.target.value})} required />
                        <input type="password" placeholder="Secret Key" value={alpacaForm.secret_key} onChange={e => setAlpacaForm({...alpacaForm, secret_key: e.target.value})} required />
                        <div className="checkbox-group"><input type="checkbox" id="is_paper_alpaca" checked={alpacaForm.is_paper} onChange={e => setAlpacaForm({...alpacaForm, is_paper: e.target.checked})} /><label htmlFor="is_paper_alpaca">Paper Trading Account</label></div>
                        <button type="submit" className="control-button">Save Alpaca Connection</button>
                    </form>
                )}
                 {showIbkrForm && (
                    <form onSubmit={(e) => handleAddConnection(e, 'IBKR')} className="add-connection-form">
                        <input type="text" placeholder="Account Name (e.g., My IBKR Account)" value={ibkrForm.account_name} onChange={e => setIbkrForm({...ibkrForm, account_name: e.target.value})} required />
                        <input type="text" placeholder="Host (e.g., 127.0.0.1)" value={ibkrForm.host} onChange={e => setIbkrForm({...ibkrForm, host: e.target.value})} required />
                        <input type="number" placeholder="Port (e.g., 7497)" value={ibkrForm.port} onChange={e => setIbkrForm({...ibkrForm, port: e.target.value})} required />
                        <input type="number" placeholder="Client ID" value={ibkrForm.client_id} onChange={e => setIbkrForm({...ibkrForm, client_id: e.target.value})} required />
                        <button type="submit" className="control-button">Save IBKR Connection</button>
                    </form>
                )}
            </div>}

            <div className="card" style={{marginTop: '2rem'}}>
                 <h2><FiShield className="icon"/> Risk & Position Sizing</h2>
                 <div className="form-grid two-cols">
                    <div className="form-group"><label htmlFor="portfolio-risk-limit">Total Open + Pending Risk Limit ($)</label><input id="portfolio-risk-limit" type="number" name="portfolio_risk_limit" min="0" value={globalSettings.portfolio_risk_limit ?? ''} onChange={handleGlobalSettingsChange}/></div>
                    <div className="form-group"><label htmlFor="risk-account">Expected Paper Account (DU…)</label><input id="risk-account" name="risk_account_id" value={globalSettings.risk_account_id ?? ''} onChange={handleGlobalSettingsChange}/></div>
                    <div className="form-group"><label htmlFor="cost-reserve">Estimated Round-trip Costs per Ticket ($)</label><input id="cost-reserve" type="number" name="cost_reserve" min="0" step="0.01" value={globalSettings.cost_reserve ?? 0} onChange={handleGlobalSettingsChange}/></div>
                    <div className="form-group checkbox-group"><label htmlFor="scope-confirmed">I have reviewed all holdings and orders in this USD paper account. Untracked exposure blocks new entries.</label><input id="scope-confirmed" type="checkbox" name="account_scope_confirmed" checked={globalSettings.account_scope_confirmed === true} onChange={handleGlobalSettingsChange}/></div>
                 </div>
                 <div className="form-grid four-cols">
                    <div className="form-group">
                        <label>Total Portfolio Value ($)</label>
                        <input type="number" name="portfolio_value" min="0" value={globalSettings.portfolio_value} onChange={handleGlobalSettingsChange} />
                    </div>
                     <div className="form-group">
                        <label>Max Position Size (% of Portfolio)</label>
                        <input type="number" name="max_pos_percent" min="0" value={globalSettings.max_pos_percent} onChange={handleGlobalSettingsChange} />
                    </div>
                     <div className="form-group">
                        <label>Max Daily Loss (% of Portfolio)</label>
                        <input type="number" name="max_daily_loss_percent" min="0" value={globalSettings.max_daily_loss_percent} onChange={handleGlobalSettingsChange} />
                    </div>
                    <div className="form-group checkbox-group">
                        <label htmlFor="kill-switch-check">Enable Daily Loss Kill Switch</label>
                        <input type="checkbox" name="kill_switch_enabled" checked={globalSettings.kill_switch_enabled} onChange={handleGlobalSettingsChange} id="kill-switch-check"/>
                    </div>
                 </div>
                 <div className="form-grid two-cols" style={{marginTop: '1.5rem', borderTop: '1px solid var(--border-primary)', paddingTop: '1.5rem'}}>
                    <div className="form-group">
                        <label>Risk Sizing Model</label>
                        <select name="risk_model" value={globalSettings.risk_model} onChange={handleGlobalSettingsChange}>
                            <option value="fixed_amount">Fixed Amount ($)</option>
                            <option value="percentage">Percentage of Portfolio (%)</option>
                        </select>
                    </div>
                    <div className="form-group">
                        <label>Risk Amount per Trade ({globalSettings.risk_model === 'fixed_amount' ? '$' : '%'})</label>
                         <input type="number" name="risk_value" min="0" value={globalSettings.risk_value} onChange={handleGlobalSettingsChange} />
                    </div>
                 </div>
                 <div className="form-grid two-cols" style={{marginTop: '1.5rem', borderTop: '1px solid var(--border-primary)', paddingTop: '1.5rem'}}>
                    <div className="form-group checkbox-group">
                        <label htmlFor="execution-enabled-check">
                            Enable Broker Execution
                            <span className="checkbox-description">
                                Turn this off to use AlgoStock only for market intelligence, stock picking and research. No broker connection is required.
                            </span>
                        </label>
                        <input
                            type="checkbox"
                            name="execution_enabled"
                            id="execution-enabled-check"
                            disabled={!isLocalExecution}
                            checked={globalSettings.execution_enabled !== false}
                            onChange={handleGlobalSettingsChange}
                        />
                    </div>
                 </div>
                 <div className="settings-actions">
                    <button className="control-button" onClick={handleSaveGlobalSettings}><FiSave/> Save Global Settings</button>
                 </div>
            </div>

            <div className="card" style={{marginTop: '2rem'}}>
                <h2><FiRepeat className="icon"/> Re-entry Rules</h2>
                <p>Automatic re-entry is disabled in this foundation release. Original trade intent is preserved after a stop-out.</p>
            </div>

            <div className="card" style={{marginTop: '2rem'}}>
                <h2><FiTarget className="icon"/> Partial Profit Profiles</h2>
                <p className="page-subtitle" style={{margin: '0 0 1.5rem 0'}}>Define how the bot takes partial profits based on R-multiple targets.</p>
                {riskProfiles.map((profile, profileIndex) => (
                    <div key={profileIndex} className="profile-item">
                        <input 
                            className="profile-name-input"
                            type="text" 
                            value={profile.name}
                            onChange={(e) => handleProfileNameChange(profileIndex, e.target.value)}
                        />
                        <div className="targets-table">
                            <div className="target-row header">
                                <span>R-Multiple Target</span>
                                <span>Sell % of Position</span>
                                <span style={{textAlign: 'center'}}>Action</span>
                            </div>
                            {Object.entries(profile.targets).sort(([a], [b]) => a - b).map(([r, pct]) => (
                                <div key={r} className="target-row">
                                    <input 
                                        className='mono-font' 
                                        type="number" 
                                        step="0.1"
                                        value={r.toString().replace(/[^0-9.]/g, '')}
                                        onChange={(e) => handleTargetValueChange(profileIndex, r, 'r', e.target.value)}
                                    />
                                    <div className='input-with-adornment'>
                                        <input 
                                            type="number" 
                                            value={Math.round((pct || 0) * 100)} 
                                            onChange={(e) => handleTargetValueChange(profileIndex, r, 'pct', e.target.value)} 
                                        />
                                        <span>%</span>
                                    </div>
                                    <button onClick={() => handleRemoveTarget(profileIndex, r)} className="icon-button danger"><FiTrash2/></button>
                                </div>
                            ))}
                        </div>
                        <button className="control-button small" style={{marginTop: '1rem', width: 'auto'}} onClick={() => handleAddTarget(profileIndex)}>
                            <FiPlus/> Add Target
                        </button>
                    </div>
                ))}
                <div className="settings-actions">
                     <button className="control-button" onClick={handleSaveRiskProfiles}><FiSave/> Save Risk Profiles</button>
                </div>
            </div>

            <TwoFactorAuthModal 
                isOpen={is2FAModalOpen}
                onClose={() => setIs2FAModalOpen(false)}
                onComplete={handleRefreshUserData}
            />
        </div>
    );
};

export default Settings;